import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import { HERO_STATS, VIDEO_HERO_SHOWREEL } from "../data/mock";
import { api } from "../lib/api";
import AnimatedCounter from "./AnimatedCounter";
import BrandBubbles from "./BrandBubbles";

const Hero = () => {
  const videoRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState(VIDEO_HERO_SHOWREEL);

  useEffect(() => {
    api
      .get("/site-config")
      .then((r) => {
        const url = r.data?.hero_video_url;
        if (url) setVideoUrl(url);
      })
      .catch(() => {});
  }, []);

  return (
    <section
      id="home"
      className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden"
    >
      {/* Ambient background blobs */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-[-10%] left-[50%] -translate-x-1/2 w-[1200px] h-[1200px] rounded-full bg-[radial-gradient(circle_at_center,rgba(255,180,160,0.5),rgba(255,200,180,0.22)_35%,rgba(255,255,255,0)_65%)] animate-blob-slow" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle_at_center,rgba(236,72,153,0.16),rgba(255,255,255,0)_60%)] animate-blob-fast" />
        <div className="absolute top-[20%] left-[-15%] w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle_at_center,rgba(168,85,247,0.12),rgba(255,255,255,0)_60%)] animate-blob-mid" />
      </div>

      <div className="max-w-[1300px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 md:gap-16 items-center">
        {/* LEFT: Copy */}
        <div className="lg:col-span-7 text-center lg:text-left" data-testid="hero-copy">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/70 backdrop-blur border border-black/5 text-[10px] uppercase tracking-[0.28em] font-bold text-neutral-700 shadow-[0_4px_20px_-6px_rgba(0,0,0,0.06)]">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-pink-500" />
            </span>
            Booking Q3-Q4 · noch 18 Slots frei
          </div>

          <h1 className="mt-6 font-display font-bold tracking-[-0.035em] leading-[0.98] text-[44px] sm:text-[64px] md:text-[80px] lg:text-[92px] text-neutral-950">
            Content,{" "}
            <span className="bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 bg-clip-text text-transparent">
              der verkauft.
            </span>
          </h1>

          <p className="mt-6 max-w-xl text-base md:text-lg text-neutral-600 leading-relaxed font-normal mx-auto lg:mx-0">
            Wir machen eure Marke auf Social Media sichtbar – mit Strategie,
            die Anfragen, Buchungen und Umsatz bringt. Von Aldi bis Five Guys.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start items-center">
            <Link
              to="/kontakt"
              className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white text-sm font-bold tracking-[0.12em] uppercase shadow-[0_12px_32px_-10px_rgba(236,72,153,0.55)] hover:shadow-[0_18px_42px_-10px_rgba(236,72,153,0.75)] hover:-translate-y-[2px] transition-all duration-300"
              data-testid="hero-cta-primary"
            >
              Kostenlos beraten lassen
              <ArrowRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
            <Link
              to="/erfolge"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full border-2 border-neutral-950 text-neutral-950 text-sm font-bold tracking-[0.12em] uppercase bg-white/60 backdrop-blur hover:bg-neutral-950 hover:text-white transition-all duration-300"
              data-testid="hero-cta-secondary"
            >
              Erfolge ansehen
            </Link>
          </div>

          {/* Stats row */}
          <div className="mt-12 grid grid-cols-3 gap-3 md:gap-8 max-w-xl mx-auto lg:mx-0">
            {HERO_STATS.map((s, idx) => (
              <div key={idx} className="flex flex-col items-center lg:items-start">
                <div className="flex items-baseline gap-1">
                  <AnimatedCounter
                    value={s.raw}
                    format={s.format}
                    decimals={s.decimals || 0}
                    suffix={s.suffix || ""}
                    duration={1800 + idx * 200}
                    className={`font-display font-extrabold text-2xl md:text-4xl tracking-tight ${s.color}`}
                  />
                  {s.showStar && (
                    <Star
                      size={22}
                      className="fill-amber-400 text-amber-400 md:w-7 md:h-7"
                    />
                  )}
                </div>
                <span className="mt-1 text-[11px] md:text-xs text-neutral-500 font-medium">
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT: Video + floating brand bubbles */}
        <div className="lg:col-span-5" data-testid="hero-video-panel">
          <div className="relative">
            <BrandBubbles />
            <div
              className="relative aspect-[9/16] rounded-[28px] overflow-hidden bg-neutral-950 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.55)] ring-1 ring-black/10 group"
              data-testid="hero-video-container"
            >
              <video
                ref={videoRef}
                src={videoUrl}
                key={videoUrl}
                autoPlay
                muted
                playsInline
                loop
                preload="auto"
                poster=""
                className="absolute inset-0 w-full h-full object-contain bg-black"
                style={{ opacity: 0.92 }}
              />
              {/* Soft cinematic gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent pointer-events-none" />

              {/* Bottom label */}
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between pointer-events-none">
                <div className="px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-[10px] uppercase tracking-[0.22em] font-bold text-neutral-950">
                  Live Showreel
                </div>
                <div className="px-3 py-1.5 rounded-full bg-black/60 backdrop-blur text-[10px] uppercase tracking-[0.2em] font-bold text-white border border-white/10">
                  FREEAYTO · Best-of
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
